import { create } from 'zustand';
import { isStandalone } from './push';

/** Shown when the browser refused the microphone (the permission is off for the site). */
export const useMediaHelp = create<{ open: boolean; video: boolean }>(() => ({ open: false, video: false }));

export const showMediaHelp = (video = false) => useMediaHelp.setState({ open: true, video });
export const hideMediaHelp = () => useMediaHelp.setState({ open: false });

/** The user (or a setting) blocked access, as opposed to a missing or busy device. */
export const isPermissionDenied = (err: unknown) =>
  err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'SecurityError');

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = () => /android/i.test(navigator.userAgent);

/** Where to turn the microphone back on, for the device and the way the app is opened. */
export function mediaHelpSteps(): { steps: string[]; after: string } {
  const host = location.host;
  if (isIos()) {
    return isStandalone()
      ? {
          steps: [
            'Откройте «Настройки» iPhone → «Приложения» → «Safari» (в iOS 17 и раньше — сразу «Safari»).',
            '«Микрофон» (и «Камера» для видеозвонков) → «Разрешить».',
          ],
          after: 'Затем закройте Evo (смахните из списка приложений) и откройте снова.',
        }
      : {
          steps: ['Нажмите «аА» слева от адреса сайта → «Настройки веб-сайта».', '«Микрофон» (и «Камера») → «Разрешить».'],
          after: 'Затем обновите страницу.',
        };
  }
  if (isAndroid()) {
    return isStandalone()
      ? {
          steps: [
            'Нажмите и удерживайте значок Evo на рабочем столе → «О приложении» (ⓘ) → «Разрешения».',
            '«Микрофон» (и «Камера») → «Разрешить» или «Разрешить только при использовании».',
            `Если там нет микрофона: Chrome → ⋮ → «Настройки» → «Настройки сайтов» → «Микрофон» → ${host} → «Разрешить».`,
          ],
          after: 'Затем закройте Evo и откройте снова.',
        }
      : {
          steps: ['Нажмите на значок слева от адреса сайта → «Разрешения».', '«Микрофон» (и «Камера») → «Разрешить».'],
          after: 'Затем обновите страницу.',
        };
  }
  return {
    steps: ['Нажмите на значок слева от адреса сайта (замок или настройки).', '«Микрофон» (и «Камера») → «Разрешить».'],
    after: 'Затем обновите страницу.',
  };
}

/** Asks for the microphone once and releases it right away: true when access is granted now. */
export async function checkMicrophone(): Promise<boolean> {
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
}
